import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight,
  Users,
  Award,
  Globe2,
  Star,
  Car,
  Shield,
  MapPin,
  Phone,
  Mail,
  Instagram,
  Facebook,
  Youtube,
  ChevronLeft,
} from 'lucide-react';
import { FaTiktok, FaXTwitter, FaWhatsapp } from 'react-icons/fa6';
import ScrollToTop from '../../components/ScrollToTop';
import { useTranslation } from 'react-i18next';
import SEOHead from '../../components/SEOHead';
import SEOKeywordContent from '../../components/SEOKeywordContent';
import toast from '../../utils/toastUtils';
import useEmblaCarousel from 'embla-carousel-react';
import featuresData from '../../components/data/featuresData';

// Static social media data
const socialMediaIcons = [
  { 
    icon: <Instagram className="w-4 h-4 sm:w-5 sm:h-5" />, 
    color: 'hover:text-pink-600',
    href: 'https://www.instagram.com/ab_drive.rw/',
    show: true
  },
  { 
    icon: <FaTiktok className="w-4 h-4 sm:w-5 sm:h-5" />, 
    color: 'hover:text-black',
    href: 'https://www.tiktok.com/@abdrive_rw3?is_from_webapp=1&sender_device=pc',
    show: true
  },
  { 
    icon: <FaXTwitter className="w-4 h-4 sm:w-5 sm:h-5" />, 
    color: 'hover:text-gray-800',
    href: 'https://x.com/ab_drive_rw',
    show: true
  },
  { 
    icon: <Facebook className="w-4 h-4 sm:w-5 sm:h-5" />, 
    color: 'hover:text-blue-600',
    href: '#',
    show: false
  },
  { 
    icon: <Youtube className="w-4 h-4 sm:w-5 sm:h-5" />, 
    color: 'hover:text-red-600',
    href: '#',
    show: false
  },
  { 
    icon: <FaWhatsapp className="w-4 h-4 sm:w-5 sm:h-5" />, 
    color: 'hover:text-green-600',
    href: '#',
    show: false
  },
];

const LANGUAGE_MAP = {
  en: {
    code: 'en',
    locale: 'en',
    howItWorksFolder: 'English',
    fullLabel: 'English',
  },
  fr: {
    code: 'fr',
    locale: 'fr',
    howItWorksFolder: 'French',
    fullLabel: 'Français',
  },
  kiny: {
    code: 'kiny',
    locale: 'rw',
    howItWorksFolder: 'Kinyarwanda',
    fullLabel: 'Kinyarwanda',
  },
};

const normalizeLanguageCode = (language = 'rw') => {
  const base = language.split('-')[0]?.toLowerCase() || 'rw';
  if (base === 'fr') return 'fr';
  if (base === 'en' || base === 'eng') return 'en';
  if (base === 'rw' || base === 'kiny' || base === 'kin') return 'kiny';
  return 'kiny';
};

const resolveLanguageContext = (language) => {
  const normalized = normalizeLanguageCode(language);
  return LANGUAGE_MAP[normalized] || LANGUAGE_MAP.kiny;
};

const getFeaturesLanguageKey = (languageContext) => {
  if (languageContext?.code === 'fr') return 'fr';
  if (languageContext?.code === 'en') return 'en';
  return 'kiny';
};

const buildSeoCopy = (type, languageContext, t, options = {}) => {
  const brandName = t?.('brandName') || 'AB Drive';
  const featureLabel = options.featureKey ? t?.(options.featureKey) : '';

  const localizedStrings = {
    logo: {
      en: {
        alt: `${brandName} | Rwanda driving school logo for online provisional driving license preparation`,
        description: `${brandName} helps learners master Rwanda traffic rules, Ikizamini, Ibyapa, Twara and the provisional driving license exam.`,
        keywords: 'AB Drive, Rwanda driving school, provisional driving license, Ikizamini, Ibyapa, Twara',
      },
      fr: {
        alt: `${brandName} | Logo de l'auto-école Rwanda pour la préparation du permis provisoire en ligne`,
        description: `${brandName} accompagne les apprenants du Rwanda pour réussir le code de la route, Ikizamini, Ibyapa, Twara et le permis provisoire.`,
        keywords: 'AB Drive, auto-école Rwanda, permis provisoire, Ikizamini, Ibyapa, Twara',
      },
      kiny: {
        alt: `${brandName} | Logo y'ishuri ry'amategeko y'umuhanda mu Rwanda ryigisha provisoire ku murongo`,
        description: `${brandName} ifasha abanyeshuri kwiga amategeko y'umuhanda, Ikizamini, Ibyapa, Twara no gutsinda uruhushya rw'agateganyo.`,
        keywords: `AB Drive, ishuri ry'amategeko y'umuhanda, Ikizamini, Ibyapa, Twara, uruhushya rw'agateganyo`,
      },
    },
    team: {
      en: {
        alt: `${brandName} training team supporting Rwanda driving license candidates`,
        description: `${brandName} instructors provide modern resources to help students master Rwanda traffic rules and the provisional driving license exam.`,
        keywords: 'AB Drive team, Rwanda driving license support, provisional driving test, Ikizamini',
      },
      fr: {
        alt: `Équipe pédagogique ${brandName} accompagnant les candidats au permis de conduire au Rwanda`,
        description: `L'équipe ${brandName} offre des ressources modernes pour réussir le code de la route, Ikizamini et le permis provisoire.`,
        keywords: 'équipe AB Drive, permis de conduire Rwanda, Ikizamini, code de la route',
      },
      kiny: {
        alt: `Itsinda ${brandName} rifasha abanyeshuri b'u Rwanda kubona uruhushya rw'agateganyo`,
        description: `Itsinda ${brandName} ritanga amasomo agezweho yo gutsinda Ikizamini, Ibyapa na Twara kugira ngo haboneke uruhushya rw'agateganyo.`,
        keywords: `itsinda AB Drive, uruhushya rw'agateganyo, Ikizamini, Ibyapa, Twara`,
      },
    },
    learning: {
      en: {
        alt: `${brandName} interactive traffic rules lessons platform for Rwanda provisional license`,
        description: `${brandName} delivers flexible learning modules covering Ikizamini, Ibyapa, Twara and mock exams for Rwanda drivers.`,
        keywords: 'traffic rules lessons, Rwanda provisional license, Ikizamini, Twara, AB Drive',
      },
      fr: {
        alt: `Plateforme ${brandName} d'apprentissage interactif du code de la route pour le permis provisoire`,
        description: `${brandName} propose des modules flexibles couvrant Ikizamini, Ibyapa, Twara et des examens blancs.`,
        keywords: 'leçons code de la route, permis provisoire Rwanda, Ikizamini, Twara, AB Drive',
      },
      kiny: {
        alt: `Urubuga rwa ${brandName} rwo kwiga amategeko y'umuhanda no kwitegura provisoire`,
        description: `${brandName} itanga amasomo yoroshya kwiga Ikizamini, Ibyapa, Twara n'ibizamini byo kwitoza.`,
        keywords: `amasomo y'amategeko y'umuhanda, provisoire Rwanda, Ikizamini, Twara, AB Drive`,
      },
    },
    contact: {
      en: {
        alt: `Contact ${brandName} Rwanda driving school support team`,
        description: `Reach ${brandName} for guidance on Rwanda provisional driving lessons, Ikizamini practice, Ibyapa and Twara revision.`,
        keywords: 'contact AB Drive, Rwanda driving school support, Ikizamini help, provisional license',
      },
      fr: {
        alt: `Contactez l'équipe support ${brandName} pour le permis de conduire au Rwanda`,
        description: `Contactez ${brandName} pour recevoir un accompagnement sur les leçons Ikizamini, Ibyapa, Twara et le permis provisoire.`,
        keywords: 'contact AB Drive, support auto-école Rwanda, Ikizamini, permis provisoire',
      },
      kiny: {
        alt: `Twandikire ${brandName} kugirango ubone ubufasha bwo kwiga amategeko y'umuhanda`,
        description: `Vugana na ${brandName} ubone ubufasha ku masomo ya Ikizamini, Ibyapa, Twara n'uruhushya rw'agateganyo.`,
        keywords: `twandikire AB Drive, ubufasha bwo kwiga amategeko y'umuhanda, Ikizamini, Twara, provisoire`,
      },
    },
    feature: {
      en: {
        description: `helps Rwanda learners master traffic rules, Ikizamini, Ibyapa, Twara and the provisional driving license exam.`,
        keywords: `${featureLabel}, Rwanda driving license, Ikizamini, Ibyapa, Twara, provisional exam, AB Drive`,
      },
      fr: {
        description: `aide les apprenants du Rwanda à maîtriser le code de la route, Ikizamini, Ibyapa, Twara et le permis provisoire.`,
        keywords: `${featureLabel}, permis de conduire Rwanda, Ikizamini, Ibyapa, Twara, permis provisoire, AB Drive`,
      },
      kiny: {
        description: `ifasha abanyeshuri b'u Rwanda kwiga amategeko y'umuhanda, Ikizamini, Ibyapa, Twara no gutsinda uruhushya rw'agateganyo.`,
        keywords: `${featureLabel}, Ikizamini, Ibyapa, Twara, uruhushya rw'agateganyo, Rwanda, AB Drive`,
      },
    },
  };

  const langKey = languageContext?.code || 'kiny';
  const typeStrings = localizedStrings[type]?.[langKey] || localizedStrings[type]?.kiny;

  if (!typeStrings) {
    return {
      alt: `${brandName} image`,
      title: `${brandName} image`,
      description: `${brandName} Rwanda driving school`,
      keywords: 'AB Drive, Rwanda driving school',
    };
  }

  const brandSuffix = brandName ? ` - ${brandName}` : '';
  const description = type === 'feature'
    ? `${featureLabel} ${typeStrings.description}${brandSuffix}`
    : typeStrings.description;

  const alt = type === 'feature'
    ? `${featureLabel} - ${typeStrings.description}${brandSuffix}`
    : typeStrings.alt;

  return {
    alt,
    title: type === 'feature' ? `${featureLabel} | ${brandName}` : typeStrings.alt,
    description,
    keywords: typeStrings.keywords,
  };
};

// Custom Hook to Detect Mobile
const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < breakpoint;
    }
    return false;
  });

  const handleResize = useCallback(() => {
      setIsMobile(window.innerWidth < breakpoint);
  }, [breakpoint]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  return isMobile;
};

// Features Component (Inline)
const FeaturesSection = React.memo(({ t, languageContext, isMobile }) => {
  const [, featuresEmbla] = useEmblaCarousel({ loop: true });
  
  const featuresLanguageKey = useMemo(
    () => getFeaturesLanguageKey(languageContext),
    [languageContext]
  );
  
  const features = useMemo(() => 
    featuresData[featuresLanguageKey]?.[isMobile ? 'mobile' : 'desktop'] || featuresData.en.desktop,
    [featuresLanguageKey, isMobile]
  );

  const getFeatureSeoData = useCallback(
    (feature) => buildSeoCopy('feature', languageContext, t, { featureKey: feature.key }),
    [languageContext, t]
  );

  const getOptimizedImageSrc = useCallback((originalSrc) => {
    if (originalSrc.includes('.png') || originalSrc.includes('.jpg') || originalSrc.includes('.jpeg')) {
      return originalSrc.replace(/\.(png|jpg|jpeg)$/i, '.webp');
    }
    return originalSrc;
  }, []);

  const localizedHiddenDescription = useMemo(() => {
    if (languageContext.code === 'fr') {
      return 'Fonctionnalités de préparation permis de conduire Rwanda incluant cours interactifs, examens pratiques et modules de formation pour auto-école Rwanda.';
    }
    if (languageContext.code === 'en') {
      return 'Rwanda traffic rules driving license preparation features including interactive courses, practice exams, and comprehensive training modules for provisional driving test Rwanda.';
    }
    return 'Ibiranga amasomo y\'amategeko y\'umuhanda mu Rwanda harimo amasomo ageramiwe, ibizamini, n\'uburyo bwo kwiga ikizamini cy\'uruhushya rw\'agateganyo.';
  }, [languageContext.code]);

  const scrollNext = useCallback(() => {
    featuresEmbla?.scrollNext();
  }, [featuresEmbla]);

  useEffect(() => {
    if (!featuresEmbla) return;
    const interval = setInterval(scrollNext, 3000);
    return () => clearInterval(interval);
  }, [featuresEmbla, scrollNext]);

  return (
    <section 
      id="features" 
      className="py-16 bg-[#e0e5ec] scroll-section"
      itemScope 
      itemType="https://schema.org/ItemList"
    >
      <SEOHead page="features" />
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
        <h2 
          className="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-gradient mb-2 animate-slideInDown font-rounded px-2"
          itemProp="name"
        >
          {t('ourFeatures')}
        </h2>
        <div className="h-1 w-16 sm:w-20 md:w-24 mx-auto bg-gradient-to-r from-blue-500 to-blue-300 rounded-full mb-8 sm:mb-10 md:mb-12" />
        
        <div style={{ display: 'none' }} itemProp="description">
          {localizedHiddenDescription}
        </div>

        <div className={isMobile ? 'grid grid-cols-1 gap-4 sm:gap-6' : 'grid md:grid-cols-2 gap-6 md:gap-8'}>
          {features.map((feature, i) => {
            const featureSeo = getFeatureSeoData(feature);
            return (
            <div
              key={feature.key ?? i}
              className={
                'transform hover:translate-y-[-8px] transition-all duration-300 animate-fadeInUp ' +
                (isMobile ? '' : '')
              }
              style={{ animationDelay: feature.animationDelay }}
              itemScope
              itemType="https://schema.org/ImageObject"
              itemProp="itemListElement"
            >
              <div
                className="neumorphic rounded-xl sm:rounded-2xl overflow-hidden relative"
                style={isMobile ? { paddingTop: '133%' } : { paddingTop: '56.25%' }}
              >
                <picture>
                  <source 
                    srcSet={getOptimizedImageSrc(feature.image)} 
                    type="image/webp"
                  />
                  <img
                    src={feature.image}
                    alt={featureSeo.alt}
                    title={featureSeo.title}
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                    className="absolute inset-0 w-full h-full object-cover"
                    itemProp="image"
                    data-language={languageContext.code}
                    data-device-type={isMobile ? 'mobile' : 'desktop'}
                    data-feature-key={feature.key}
                    data-seo-keywords={featureSeo.keywords}
                  />
                </picture>
                <h3 
                  className="absolute bottom-2 sm:bottom-4 left-2 sm:left-4 bg-gradient-to-r from-blue-500 to-blue-400 text-white font-bold px-2 sm:px-3 py-1 rounded text-xs sm:text-sm md:text-base"
                  itemProp="name"
                >
                  {t(feature.key)}
                </h3>
                
                <meta itemProp="contentUrl" content={feature.image} />
                <meta itemProp="description" content={featureSeo.description} />
                <meta itemProp="keywords" content={featureSeo.keywords} />
              </div>
            </div>
            );
          })}
        </div>
      </div>
    </section>
  );
});

FeaturesSection.displayName = 'FeaturesSection';

// Why Choose Us Component (Inline)
const WhyChooseUsSection = React.memo(({ t }) => {
  const features = useMemo(() => [
    { title: t('multipleLanguages'), icon: <Users />, description: t('multipleLanguagesdes') },
    { title: t('flexibleLearning'), icon: <Car />, description: t('flexiblelearningdes') },
    { title: t('allExams'), icon: <Award />, description: t('allExamsdes') },
    { title: t('24/7Support'), icon: <Shield />, description: t('24/7Supportdes') },
  ], [t]);

  return (
    <section className="py-8 sm:py-10 md:py-12 bg-[#e0e5ec] scroll-section">
      <SEOHead page="why-choose-us" />
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-center text-gradient mb-2 animate-slideInDown font-rounded px-2">
          {t('whyChooseUs')}
        </h2>
        <div className="h-1 w-16 sm:w-20 md:w-24 mx-auto bg-gradient-to-r from-blue-500 to-blue-300 rounded-full mb-8 sm:mb-10 md:mb-12"></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
          {features.map((feature, index) => (
            <div
              key={feature.title}
              className="text-center neumorphic p-5 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl hover:scale-105 transition-transform duration-300 animate-fadeInUp"
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 neumorphic rounded-full mb-3 sm:mb-4 text-blue-600">
                {feature.icon}
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 px-2">{feature.title}</h3>
              <p className="text-sm sm:text-base text-gray-600 px-2">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
});

WhyChooseUsSection.displayName = 'WhyChooseUsSection';

// Testimonials Component (Inline)
const TestimonialsSection = React.memo(({ t }) => {
  const [testimonialsCarouselRef, testimonialsEmbla] = useEmblaCarousel({ loop: true });

  const testimonials = useMemo(() => [
    { name: 'Giramata Peace', quote: t('testimonial1'), rating: 5, role: t('student') },
    { name: 'Shyaka Kenneth', quote: t('testimonial2'), rating: 4, role: t('teacher') },
    { name: 'Shema Davis', quote: t('testimonial3'), rating: 5, role: t('student') },
    { name: 'Rachel', quote: t('testimonial4'), rating: 4, role: t('student') },
    { name: 'Sarah Noella', quote: t('testimonial5'), rating: 5, role: t('student') },
    { name: 'David King', quote: t('testimonial6'), rating: 4, role: t('teacher') },
    { name: 'Manzi Herve', quote: t('testimonial7'), rating: 5, role: t('student') },
  ], [t]);

  const scrollNext = useCallback(() => {
    testimonialsEmbla?.scrollNext();
  }, [testimonialsEmbla]);

  const scrollPrev = useCallback(() => {
    testimonialsEmbla?.scrollPrev();
  }, [testimonialsEmbla]);

  useEffect(() => {
    if (testimonialsEmbla) {
      const interval = setInterval(scrollNext, 3000);
      return () => clearInterval(interval);
    }
  }, [testimonialsEmbla, scrollNext]);

  return (
    <section className="py-8 sm:py-10 md:py-12 bg-[#e0e5ec] scroll-section">
      <SEOHead page="testimonials" />
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-center text-gradient mb-2 animate-slideInDown font-rounded px-2">
          {t('Testimonials')}
        </h2>
        <div className="h-1 w-16 sm:w-20 md:w-24 mx-auto bg-gradient-to-r from-blue-500 to-blue-300 rounded-full mb-8 sm:mb-10 md:mb-12"></div>
        <div className="relative px-4 sm:px-0">
          <div className="embla" ref={testimonialsCarouselRef}>
            <div className="embla__container flex">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="embla__slide flex-shrink-0 w-full sm:w-4/5 md:w-auto px-2">
                  <div className="neumorphic p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl hover:scale-105 transition-transform duration-300 animate-fadeInUp">
                    <div className="flex items-center mb-4 sm:mb-6">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 neumorphic rounded-full flex items-center justify-center text-blue-600 flex-shrink-0">
                        <Users className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8" />
                      </div>
                      <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                        <p className="font-semibold text-gray-900 text-sm sm:text-base truncate">{testimonial.name}</p>
                        <p className="text-gray-600 text-xs sm:text-sm">{testimonial.role}</p>
                        <div className="flex mt-1">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <Star key={i} className="h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5 text-yellow-400 fill-current" />
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-gray-600 italic text-sm sm:text-base">"{testimonial.quote}"</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <button
            onClick={scrollPrev}
            className="absolute left-0 sm:-left-4 top-1/2 -translate-y-1/2 neumorphic-button p-2 sm:p-3 rounded-full z-10"
            aria-label="Previous testimonial"
          >
            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-600" />
          </button>
          <button
            onClick={scrollNext}
            className="absolute right-0 sm:-right-4 top-1/2 -translate-y-1/2 neumorphic-button p-2 sm:p-3 rounded-full z-10"
            aria-label="Next testimonial"
          >
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-600" />
          </button>
        </div>
      </div>
    </section>
  );
});

TestimonialsSection.displayName = 'TestimonialsSection';

// Contact Component (Inline)
const ContactSection = React.memo(({ t, languageContext }) => {
  const contactSeo = useMemo(
    () => buildSeoCopy('contact', languageContext, t),
    [languageContext, t]
  );
  const [contactFormData, setContactFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    message: '',
    type: "contact"
  });
  const [contactStatus, setContactStatus] = useState('');

  const handleContactChange = useCallback((e) => {
    const { name, value } = e.target;
    setContactFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const resetForm = useCallback(() => {
    setContactFormData({ fullName: '', email: '', phone: '', message: '', type: "contact" });
  }, []);

  const setStatus = useCallback((status) => {
    setContactStatus(status);
    if (status !== 'sending') {
      setTimeout(() => setContactStatus(''), 3000);
    }
  }, []);

  const handleContactSubmit = useCallback(async (e) => {
    e.preventDefault();
    setContactStatus('sending');
    try {
      const apiUrl = process.env.REACT_APP_API_BASE_URL || 'https://backend-neoeloi-neoelois-projects.vercel.app/api';
      const response = await fetch(`${apiUrl}/support/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactFormData),
      });
      if (response.ok) {
        setStatus('success');
        resetForm();
      } else {
        setStatus('failed');
      }
    } catch (err) {
      console.error(err);
      setStatus('failed');
    }
  }, [contactFormData, setStatus, resetForm]);

  const buttonText = useMemo(() => {
    switch (contactStatus) {
      case 'sending': return t('sending');
      case 'success': return t('messageSent');
      case 'failed': return t('messageFailed');
      default: return t('sendMessage');
    }
  }, [contactStatus, t]);

  const buttonClassName = useMemo(() => {
    const base = 'w-full px-6 py-3 neumorphic-button rounded-lg transition-all duration-300';
    switch (contactStatus) {
      case 'success': return `${base} text-green-600`;
      case 'failed': return `${base} text-red-600`;
      default: return `${base} text-blue-600 hover:scale-105`;
    }
  }, [contactStatus]);

  return (
    <section id="contact" className="py-8 sm:py-10 md:py-12 bg-[#e0e5ec] scroll-section">
      <SEOHead page="contact" />
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 md:gap-12">
          <div className="neumorphic p-5 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl hover:scale-105 transition-transform duration-300 animate-fadeInUp">
            <h2 className="text-2xl sm:text-3xl font-bold text-gradient mb-4 sm:mb-6">{t('contactUs')}</h2>
            <form onSubmit={handleContactSubmit} className="space-y-4 sm:space-y-6">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  {t('fullName')}
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={contactFormData.fullName}
                  onChange={handleContactChange}
                  placeholder={t('enterFullName')}
                  className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base rounded-lg input-focus neumorphic-input"
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  {t('email')}
                </label>
                <input
                  type="email"
                  name="email"
                  value={contactFormData.email}
                  onChange={handleContactChange}
                  placeholder={t('enterEmail')}
                  className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base rounded-lg input-focus neumorphic-input"
                  required
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  {t('phoneNumber')}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={contactFormData.phone}
                  onChange={handleContactChange}
                  placeholder={t('enterPhoneNumber')}
                  className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base rounded-lg input-focus neumorphic-input"
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  {t('message')}
                </label>
                <textarea
                  name="message"
                  rows={4}
                  value={contactFormData.message}
                  onChange={handleContactChange}
                  placeholder={t('yourMessageHere')}
                  className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base rounded-lg input-focus neumorphic-input resize-none"
                  required
                />
              </div>
              <button
                type="submit"
                className={buttonClassName + ' text-sm sm:text-base'}
                disabled={contactStatus === 'sending'}
              >
                {buttonText}
              </button>
            </form>
          </div>
          <div className="flex flex-col space-y-4 sm:space-y-6">
            <div className="neumorphic p-5 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl hover:scale-105 transition-transform duration-300 animate-fadeInUp">
              <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-3 sm:mb-4 md:mb-6">
                {t('getInTouch')}
              </h3>
              <div className="space-y-2 sm:space-y-3 md:space-y-4">
                <p className="text-gray-600 text-xs sm:text-sm md:text-base flex items-start sm:items-center">
                  <MapPin className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2 flex-shrink-0 mt-0.5 sm:mt-0" />
                  <span><strong>{t('location')}:</strong> {t('kigaliRwanda')}</span>
                </p>
                <p className="text-gray-600 text-xs sm:text-sm md:text-base flex items-start sm:items-center">
                  <Phone className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2 flex-shrink-0 mt-0.5 sm:mt-0" />
                  <span><strong>{t('callUs')}:</strong> <span className="break-all">{t('0790468644 / 0791368016')}</span></span>
                </p>
                <p className="text-gray-600 text-xs sm:text-sm md:text-base flex items-start sm:items-center">
                  <Mail className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2 flex-shrink-0 mt-0.5 sm:mt-0" />
                  <span><strong>{t('emailUs')}:</strong> <span className="break-all">{t('abdrivecourse@gmail.com')}</span></span>
                </p>
              </div>
              <div className="mt-4 sm:mt-6 md:mt-8">
                <div className="flex flex-wrap gap-2 sm:gap-3 md:space-x-3 md:space-x-4">
                  {socialMediaIcons.map((social, index) => (
                    <a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`neumorphic-button p-2 sm:p-3 rounded-full text-gray-600 ${social.color} transform hover:scale-110 transition-all duration-300 ${social.show ? '' : 'hidden'}`}
                    >
                      {social.icon}
                    </a>
                  ))}
                </div>
              </div>
            </div>
            <img
              src="/images/Contact.webp"
              alt={contactSeo.alt}
              title={contactSeo.title}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className="w-full object-contain rounded-xl sm:rounded-2xl neumorphic p-2 sm:p-4"
              itemProp="image"
              data-language={languageContext.code}
              data-seo-keywords={contactSeo.keywords}
            />
          </div>
        </div>
      </div>
    </section>
  );
});

ContactSection.displayName = 'ContactSection';

// Language Dropdown Component
const LanguageDropdown = React.memo(({ t, i18n, buttonClassName }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const languages = useMemo(() => [
    { code: 'en', label: t('english') },
    { code: 'kiny', label: t('kinyarwanda') },
    { code: 'fr', label: t('french') },
  ], [t]);

  const currentLanguage = i18n.language;
  const currentLanguageLabel = useMemo(() => 
    languages.find((lang) => lang.code === currentLanguage)?.label || currentLanguage,
    [languages, currentLanguage]
  );

  const handleClickOutside = useCallback((event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setIsOpen(false);
        }
    }, []);

  const toggleDropdown = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const handleSelectLanguage = useCallback((code) => {
      if (code !== currentLanguage) {
        i18n.changeLanguage(code);
      }
      setIsOpen(false);
  }, [currentLanguage, i18n]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

    return (
      <div className="relative z-50 w-fit min-w-[100px] max-w-[130px]" ref={dropdownRef}>
        <button 
          onClick={toggleDropdown} 
          className={`${buttonClassName} w-full justify-between min-w-0`}
          title={currentLanguageLabel}
        >
          <div className="flex items-center min-w-0 flex-1">
            <Globe2 className="w-4 h-4 mr-1 flex-shrink-0" />
            <span className="truncate text-sm">{currentLanguageLabel}</span>
          </div>
        </button>
        {isOpen && (
          <div className="absolute top-full right-0 mt-2 w-max min-w-[130px] bg-[#e0e5ec] rounded-lg shadow-lg z-50 border border-gray-200">
            {languages
              .filter((lang) => lang.code !== currentLanguage)
              .map((lang) => (
                <div
                  key={lang.code}
                  onClick={() => handleSelectLanguage(lang.code)}
                  className="cursor-pointer px-4 py-2 text-black hover:bg-gray-200 whitespace-nowrap text-sm first:rounded-t-lg last:rounded-b-lg"
                  title={lang.label}
                >
                  {lang.label}
                </div>
              ))}
          </div>
        )}
      </div>
    );
});

LanguageDropdown.displayName = 'LanguageDropdown';

function LandingPage() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [networkStatus, setNetworkStatus] = useState('online');
  const [isLoading, setIsLoading] = useState(true);
  const featuresSectionRef = useRef(null);
  const observerRef = useRef(null);
  const isMobile = useIsMobile();
  const languageContext = useMemo(
    () => resolveLanguageContext(i18n.language),
    [i18n.language]
  );
  const logoSeo = useMemo(
    () => buildSeoCopy('logo', languageContext, t),
    [languageContext, t]
  );
  const teamSeo = useMemo(
    () => buildSeoCopy('team', languageContext, t),
    [languageContext, t]
  );
  const learningSeo = useMemo(
    () => buildSeoCopy('learning', languageContext, t),
    [languageContext, t]
  );

  // Network status monitoring
  useEffect(() => {
    const handleOnline = () => {
      setNetworkStatus('online');
      toast.success('Connection restored!');
    };
    
    const handleOffline = () => {
      setNetworkStatus('offline');
      toast.error('No internet connection. Some features may not work properly.');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    setNetworkStatus(navigator.onLine ? 'online' : 'offline');

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    toast.dismiss();
  }, []);

  // Preload all images for faster loading and track loading state
  useEffect(() => {
    const preloadImages = async () => {
      setIsLoading(true);
      const imagesToPreload = [
        '/images/logo/logo.webp',
        '/images/About/team.webp',
        '/images/About/Learning.webp',
        '/images/Contact.webp',
      ];

      const deviceType = isMobile ? 'Mobile' : 'LargeScreens';
      const langName = languageContext.howItWorksFolder;

      // Preload feature images based on current language and device
      for (let i = 1; i <= 6; i++) {
        imagesToPreload.push(`/images/HowItWorks/${deviceType}/${langName}/${i}.webp`);
      }

      // Create promises for all image loads
      const imagePromises = imagesToPreload.map((src) => {
        return new Promise((resolve) => {
          // Use link preload for better browser support
          const link = document.createElement('link');
          link.rel = 'preload';
          link.as = 'image';
          link.href = src;
          if ('fetchPriority' in link) {
            link.fetchPriority = 'high';
          }
          document.head.appendChild(link);

          // Use Image object to track actual loading
          const img = new Image();
          if ('fetchPriority' in img) {
            img.fetchPriority = 'high';
          }
          img.onload = () => resolve(src);
          img.onerror = () => resolve(src); // Resolve even on error to not block loading
          img.src = src;
        });
      });

      // Wait for all images to load
      try {
        await Promise.all(imagePromises);
        
        // Wait for window load event (ensures all resources are loaded)
        const windowLoadPromise = new Promise((resolve) => {
          if (document.readyState === 'complete') {
            resolve();
          } else {
            window.addEventListener('load', resolve, { once: true });
            // Fallback timeout
            setTimeout(resolve, 2000);
          }
        });
        
        await windowLoadPromise;
        
        // Also check DOM images after a short delay to ensure they're rendered
        await new Promise((resolve) => setTimeout(resolve, 200));
        const domImages = Array.from(document.querySelectorAll('img'));
        const domImagePromises = domImages.map((img) => {
          if (img.complete && img.naturalHeight !== 0) return Promise.resolve();
          return new Promise((resolve) => {
            const timeout = setTimeout(resolve, 2000); // Max 2s wait per image
            img.onload = () => {
              clearTimeout(timeout);
              resolve();
            };
            img.onerror = () => {
              clearTimeout(timeout);
              resolve(); // Resolve on error too
            };
          });
        });
        
        await Promise.all(domImagePromises);
        
        // Small delay to ensure smooth transition
        setTimeout(() => {
          setIsLoading(false);
        }, 300);
      } catch (error) {
        console.error('Error loading images:', error);
        // Still hide spinner after timeout even if some images fail
        setTimeout(() => {
          setIsLoading(false);
        }, 2000);
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      preloadImages();
    }, 100);

    return () => clearTimeout(timer);
  }, [languageContext.code, languageContext.howItWorksFolder, isMobile]);

  const scrollToFeatures = useCallback(() => {
    featuresSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const initializeIntersectionObserver = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const sections = document.querySelectorAll('.scroll-section');
    if (sections.length === 0) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observerRef.current?.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    sections.forEach((section) => {
      observerRef.current?.observe(section);
      const rect = section.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        section.classList.add('visible');
      }
    });
  }, []);

  useEffect(() => {
    initializeIntersectionObserver();
    
    const timeouts = [
      setTimeout(initializeIntersectionObserver, 100),
      setTimeout(initializeIntersectionObserver, 500),
      setTimeout(initializeIntersectionObserver, 1000),
    ];

    return () => {
      timeouts.forEach(clearTimeout);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [initializeIntersectionObserver]);

  useEffect(() => {
    const sections = document.querySelectorAll('.scroll-section');
    sections.forEach((section) => {
      section.style.visibility = 'visible';
      section.style.opacity = '1';
      section.style.transform = 'translateY(0)';
    });
  }, []);

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 50);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Loading Spinner Component
  const LoadingSpinner = () => (
    <div className="fixed inset-0 bg-[#e0e5ec] z-50 flex items-center justify-center">
      <div className="text-center">
        <div className="relative w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 mx-auto mb-4">
          {/* Outer spinning ring */}
          <div 
            className="absolute inset-0 neumorphic rounded-full"
            style={{ 
              animation: 'spin 2s linear infinite',
            }}
          >
            <div className="absolute inset-2 neumorphic-inset rounded-full"></div>
          </div>
          {/* Inner spinning ring (reverse) */}
          <div 
            className="absolute inset-3 neumorphic rounded-full"
            style={{ 
              animation: 'spin 1.5s linear infinite reverse',
            }}
          >
            <div className="absolute inset-2 neumorphic-inset rounded-full"></div>
          </div>
          {/* Center pulsing dot */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-r from-blue-500 to-blue-400 rounded-full animate-pulse shadow-lg"></div>
          </div>
        </div>
        <p className="text-gray-600 text-sm sm:text-base md:text-lg font-semibold animate-pulse mt-4">
          {t('loading') || 'Loading...'}
        </p>
      </div>
    </div>
  );

  // Generate ImageObject schema for all images on the page
  const getAllImageSchemas = useMemo(() => {
    const baseUrl = 'https://www.abdriverw.com';
    const schemaEntries = [];
    const currentYear = new Date().getFullYear();
    const contactSeo = buildSeoCopy('contact', languageContext, t);

    const staticImages = [
      { url: '/images/logo/logo.webp', seo: logoSeo },
      { url: '/images/About/team.webp', seo: teamSeo },
      { url: '/images/About/Learning.webp', seo: learningSeo },
      { url: '/images/Contact.webp', seo: contactSeo },
    ];

    staticImages.forEach(({ url, seo }) => {
      schemaEntries.push({
        url,
        seo,
      });
    });

    const featuresLanguageKey = getFeaturesLanguageKey(languageContext);
    const featureSet = featuresData[featuresLanguageKey] || featuresData.en;
    const deviceKey = isMobile ? 'mobile' : 'desktop';
    const featureImages = featureSet?.[deviceKey] || [];

    featureImages.forEach((feature) => {
      const featureSeo = buildSeoCopy('feature', languageContext, t, { featureKey: feature.key });
      schemaEntries.push({
        url: feature.image,
        seo: featureSeo,
      });
    });

    return schemaEntries.map(({ url, seo }) => ({
      "@context": "https://schema.org",
      "@type": "ImageObject",
      "contentUrl": `${baseUrl}${url}`,
      "url": `${baseUrl}${url}`,
      "name": seo.title,
      "description": seo.description,
      "caption": seo.description,
      "alternateText": seo.alt,
      "encodingFormat": url.includes('.webp') ? "image/webp" : "image/png",
      "license": baseUrl,
      "creditText": "AB Drive",
      "creator": {
        "@type": "Organization",
        "name": "AB Drive"
      },
      "keywords": seo.keywords,
      "copyrightHolder": {
        "@type": "Organization",
        "name": "AB Drive"
      },
      "copyrightNotice": `© ${currentYear} AB Drive. All rights reserved.`,
      "acquireLicensePage": `${baseUrl}/contact`
    }));
  }, [languageContext, logoSeo, teamSeo, learningSeo, isMobile, t]);

  return (
    <div className="min-h-screen bg-[#e0e5ec] relative z-0 overflow-visible notranslate" translate="no" style={{ overflowY: 'auto', overflowX: 'hidden' }}>
      <SEOHead page="home" />
      <SEOKeywordContent page="home" />
      
      {/* ImageObject Schema for all images */}
      {getAllImageSchemas.map((imageSchema, index) => (
        <script key={index} type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(imageSchema) }} />
      ))}
      
      {/* Loading Spinner */}
      {isLoading && <LoadingSpinner />}
      
      {/* Main Content - hidden while loading */}
      <div className={isLoading ? 'opacity-0 pointer-events-none' : 'opacity-100 transition-opacity duration-500'}>
      
      {/* Header */}
      <header
        className={`fixed w-full z-40 transition-colors duration-300 ${
          scrolled ? 'bg-[#e0e5ec]/80 backdrop-blur-md shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="flex justify-between items-center h-12 md:h-16 lg:h-20">
            <div className="flex items-center space-x-1 sm:space-x-2">
              <img
                src="/images/logo/logo.webp"
                alt={logoSeo.alt}
                title={logoSeo.title}
                className="h-7 w-7 sm:h-8 sm:w-8 md:h-10 md:w-10 lg:h-12 lg:w-12 object-contain"
                loading="eager"
                fetchPriority="high"
                decoding="sync"
                itemProp="image"
                data-language={languageContext.code}
                data-seo-keywords={logoSeo.keywords}
              />
              <span className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-gradient truncate max-w-[120px] sm:max-w-none">
                {t('brandName')}
              </span>
            </div>
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-4">
              {networkStatus === 'offline' && (
                <div className="flex items-center text-red-500 text-sm">
                  <div className="w-2 h-2 bg-red-500 rounded-full mr-2 animate-pulse"></div>
                  Offline
                </div>
              )}
              <LanguageDropdown
                t={t}
                i18n={i18n}
                buttonClassName="flex items-center text-gray-600 hover:text-blue-600 transition-colors duration-300 neumorphic-button px-4 py-2 rounded-lg"
              />
              <Link to="/login">
                <button className="neumorphic-button px-4 py-2 text-gray-600 rounded-lg">
                  {t('login')}
                </button>
              </Link>
              <Link to="/signup">
                <button className="neumorphic-button px-6 py-2 text-blue-600 rounded-lg">
                  {t('signUp')}
                </button>
              </Link>
            </div>
            {/* Mobile Menu */}
            <div className="flex md:hidden items-center space-x-2">
              <LanguageDropdown
                t={t}
                i18n={i18n}
                buttonClassName="flex items-center text-gray-600 hover:text-blue-600 transition-colors duration-300 neumorphic-button px-2 py-1.5 rounded-lg text-xs"
              />
              <Link to="/login">
                <button className="neumorphic-button px-2.5 py-1.5 text-gray-600 rounded-lg text-xs sm:text-sm">
                  {t('signIn')}
                </button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-20 sm:pt-24 md:pt-32 pb-8 sm:pb-12 hero-pattern relative overflow-visible z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-500/5 to-transparent animate-heroBg z-0"></div>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 relative z-10">
          <div className="gradient-border neumorphic p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl card-hover hover:scale-105 transition-transform duration-300">
            <div className="text-center">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 transform transition-all duration-1000 animate-slideInDown leading-tight whitespace-nowrap">
                {t('driveWithCare')}
                <span className="text-gradient animate-pop inline">{t('care')}</span>
              </h1>
              <div
                className="flex flex-col sm:flex-row justify-center items-center space-y-3 sm:space-y-0 sm:space-x-4 transform transition-all duration-1000 animate-slideInUp"
                style={{ animationDelay: '0.4s' }}
              >
                <Link to="/signup" className="w-full sm:w-auto">
                  <button className="w-full sm:w-auto px-5 sm:px-6 md:px-8 py-2.5 sm:py-2 md:py-3 neumorphic-button text-blue-600 rounded-lg flex items-center justify-center hover:scale-105 transition-transform duration-300 text-sm sm:text-base">
                    {t('getStarted')}
                    <ChevronRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
                  </button>
                </Link>
                <button
                  onClick={scrollToFeatures}
                  className="w-full sm:w-auto px-5 sm:px-6 md:px-8 py-2.5 sm:py-2 md:py-3 neumorphic-button text-gray-600 rounded-lg hover:scale-105 transition-transform duration-300 text-sm sm:text-base"
                >
                  {t('learnMore')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section className="py-8 sm:py-10 md:py-12 bg-[#e0e5ec] scroll-section relative z-10" id="about-us">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
            <div className="shadow-[8px_8px_16px_#b8bec8,_-8px_-8px_16px_#ffffff] p-4 sm:p-5 rounded-xl sm:rounded-2xl transition-all duration-300 box-motion">
              <div className="flex-grow">
                <h2 className="title-gradient text-2xl sm:text-3xl font-extrabold mb-4 sm:mb-6 animate-titleIn">
                  {t('whoAreWe')}
                </h2>
                <p className="text-gray-600 mb-6 sm:mb-8 text-sm sm:text-base">{t('whoAreWeParagraph')}</p>
              </div>
              <div className="shadow-[inset_4px_4px_8px_#b8bec8,_inset_-4px_-4px_8px_#ffffff] p-3 sm:p-4 rounded-xl h-48 sm:h-56 md:h-64">
                <img
                  src="/images/About/team.webp"
                  alt={teamSeo.alt}
                  title={teamSeo.title}
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  className="w-full h-full object-contain"
                  data-language={languageContext.code}
                  data-seo-keywords={teamSeo.keywords}
                />
              </div>
            </div>
            <div className="shadow-[8px_8px_16px_#b8bec8,_-8px_-8px_16px_#ffffff] p-4 sm:p-5 rounded-xl sm:rounded-2xl transition-all duration-300 box-motion">
              <div className="flex-grow">
                <h2 className="title-gradient text-2xl sm:text-3xl font-extrabold mb-4 sm:mb-6 animate-titleIn">
                  {t('whatWeDo')}
                </h2>
                <p className="text-gray-600 mb-6 sm:mb-8 text-sm sm:text-base">{t('whatWeDoParagraph')}</p>
              </div>
              <div className="shadow-[inset_4px_4px_8px_#b8bec8,_inset_-4px_-4px_8px_#ffffff] p-3 sm:p-4 rounded-xl h-48 sm:h-56 md:h-64">
                <img
                  src="/images/About/Learning.webp"
                  alt={learningSeo.alt}
                  title={learningSeo.title}
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  className="w-full h-full object-contain"
                  itemProp="image"
                  data-language={languageContext.code}
                  data-seo-keywords={learningSeo.keywords}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <div ref={featuresSectionRef}>
        <FeaturesSection t={t} languageContext={languageContext} isMobile={isMobile} />
      </div>

      {/* Why Choose Us Section */}
      <WhyChooseUsSection t={t} />

      {/* Testimonials Section */}
      <TestimonialsSection t={t} />

      {/* Contact Section */}
      <ContactSection t={t} languageContext={languageContext} />

      {/* Improved Footer */}
      <footer className="bg-[#e0e5ec] py-8 sm:py-10 md:py-12 relative z-10 border-t border-gray-300">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-6 sm:mb-8">
            {/* Brand Section */}
            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-center space-x-1 sm:space-x-2">
                <img
                  src="/images/logo/logo.webp"
                  alt={logoSeo.alt}
                  title={logoSeo.title}
                  className="h-7 w-7 sm:h-8 sm:w-8 md:h-10 md:w-10 lg:h-12 lg:w-12 object-contain"
                  loading="eager"
                  fetchPriority="high"
                  decoding="sync"
                  itemProp="image"
                  data-language={languageContext.code}
                  data-seo-keywords={logoSeo.keywords}
                />
                <h3 className="text-xl sm:text-2xl font-bold text-gradient">
                  {t('brandName')}
                </h3>
              </div>
              <p className="text-gray-600 text-sm sm:text-base">
                {t('footerDescription')}
              </p>
            </div>

            {/* Quick Links */}
            <div className="space-y-3 sm:space-y-4">
              <h4 className="text-base sm:text-lg font-semibold text-gray-900">
                {t('quickLinks')}
              </h4>
              <ul className="grid grid-cols-2 gap-2">
                <li>
                  <a href="#features" className="text-gray-600 hover:text-blue-600 transition-colors duration-300 text-xs sm:text-sm">
                    {t('ourFeatures')}
                  </a>
                </li>
                <li>
                  <a href="#contact" className="text-gray-600 hover:text-blue-600 transition-colors duration-300 text-xs sm:text-sm">
                    {t('contactUs')}
                  </a>
                </li>
                <li>
                  <Link to="/login" className="text-gray-600 hover:text-blue-600 transition-colors duration-300 text-xs sm:text-sm">
                    {t('login')}
                  </Link>
                </li>
                <li>
                  <Link to="/signup" className="text-gray-600 hover:text-blue-600 transition-colors duration-300 text-xs sm:text-sm">
                    {t('signUp')}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Social Media */}
            <div className="space-y-3 sm:space-y-4">
              <h4 className="text-base sm:text-lg font-semibold text-gray-900">
                {t('followUs')}
              </h4>
              <div className="flex flex-wrap gap-2 sm:gap-3">
                {socialMediaIcons.filter(s => s.show).map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`neumorphic-button p-2 sm:p-3 rounded-full text-gray-600 ${social.color} transform hover:scale-110 transition-all duration-300`}
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-6 sm:pt-8 border-t border-gray-300">
            <div className="flex justify-center items-center">
              <p className="text-gray-600 text-xs sm:text-sm text-center px-2">
                © {new Date().getFullYear()} {t('brandName')}. {t('allRightsReserved')}.
              </p>
            </div>
          </div>
        </div>
      </footer>

      <ScrollToTop />
      </div>
      {/* End of Main Content wrapper */}
    </div>
  );
}

export default LandingPage;